<template>
  <g>
    <rect
      x="0"
      :y="y - hoverTargetHeight / 2"
      :width="hoverTargetWidth"
      :height="hoverTargetHeight"
      visibility="hidden"
      pointer-events="painted"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    />
    <template v-if="isSuppressed">
      <text
        x="4"
        :y="y"
        dy="0.15em"
        :font-size="suppressionSymbolFont.size"
        :font-family="suppressionSymbolFont.family"
        dominant-baseline="central"
        :fill="suppressionSymbolColor"
        pointer-events="none"
      >
        {{ suppressionSymbol }}
      </text>
    </template>
    <template v-else>
      <rect
        v-if="hasConfidenceInterval"
        :x="confidenceIntervalX.lower"
        :y="y - (ciBandHeight / 2)"
        :width="confidenceIntervalX.upper - confidenceIntervalX.lower"
        :height="ciBandHeight"
        fill="#d0e5f4"
        pointer-events="none"
      />
      <line
        x1="0"
        :x2="pointEstimateX"
        :y1="y"
        :y2="y"
        :stroke-width="lineThickness"
        :stroke="color"
        pointer-events="none"
      />
      <circle
        :cx="pointEstimateX"
        :cy="y"
        :r="circleRadius"
        :fill="color"
        pointer-events="none"
      />
      <circle
        v-if="isHovered"
        :cx="pointEstimateX"
        :cy="y"
        :r="highlightCircleRadius"
        fill="#ffffff"
        pointer-events="none"
      />
      <text
        v-if="hasBenchmark"
        :x="benchmarkX"
        :y="y"
        dy="-0.12em"
        :font-size="benchmarkSymbolFont.size"
        :font-family="benchmarkSymbolFont.family"
        text-align="middle"
        dominant-baseline="central"
        :fill="benchmarkSymbolColor"
        pointer-events="none"
      >
        {{ benchmarkSymbol }}
      </text>
    </template>
  </g>
</template>

<script>
export default {
  name: 'GraphChartBar',
  props: {
    cell: {
      type: Object,
      required: true
    },
    xScale: {
      type: Function,
      required: true
    },
    y: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    },
    color: {
      type: String,
      required: true
    },
    showConfidenceInterval: {
      type: Boolean,
      required: true
    },
    showPercentages: {
      type: Boolean,
      required: true
    },
    suppressionSymbol: {
      type: String,
      required: true
    },
    suppressionSymbolFont: {
      type: Object,
      required: true
    },
    benchmarkSymbol: {
      type: String,
      required: true
    },
    benchmarkSymbolFont: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isHovered: false
    }
  },
  computed: {
    isSuppressed () {
      return this.cell.suppressed
    },
    hasConfidenceInterval () {
      return (
        this.showConfidenceInterval &&
        !isNaN(this.confidenceInterval.lower) &&
        !isNaN(this.confidenceInterval.upper) &&
        this.confidenceInterval.lower !== null &&
        this.confidenceInterval.upper !== null
      )
    },
    pointEstimate () {
      if (this.showPercentages) {
        return this.safeProduct(100, this.cell.proportion)
      } else {
        return this.cell.numerator
      }
    },
    pointEstimateX () {
      return this.xScale(this.pointEstimate)
    },
    confidenceInterval () {
      if (this.showPercentages) {
        return {
          lower: this.safeProduct(100, this.cell.ciProportion.lower),
          upper: this.safeProduct(100, this.cell.ciProportion.upper)
        }
      } else {
        return this.cell.ciNumerator
      }
    },
    confidenceIntervalX () {
      if (this.hasConfidenceInterval) {
        return {
          lower: this.xScale(this.confidenceInterval.lower),
          upper: this.xScale(this.confidenceInterval.upper)
        }
      } else {
        return { lower: null, upper: null }
      }
    },
    lineThickness () {
      return this.isHovered ? 5 : 4
    },
    circleRadius () {
      return this.isHovered ? 8 : 5
    },
    highlightCircleRadius () {
      return 3
    },
    ciBandHeight () {
      return this.isHovered ? 16 : 12
    },
    suppressionSymbolColor () {
      return this.isHovered ? '#000000' : this.suppressionSymbolFont.color
    },
    hasBenchmark () {
      return this.showPercentages && this.cell.benchmark
    },
    benchmarkX () {
      return this.xScale(this.cell.benchmark)
    },
    benchmarkSymbolColor () {
      return this.isHovered ? '#464646' : this.benchmarkSymbolFont.color
    },
    hoverTargetHeight () {
      return this.height
    },
    hoverTargetWidth () {
      const xValues = [0]
      if (!this.isSuppressed) {
        xValues.push(this.pointEstimateX)
        if (this.hasConfidenceInterval) {
          xValues.push(this.confidenceIntervalX.upper)
        }
        if (this.hasBenchmark) {
          xValues.push(this.benchmarkX)
        }
      }
      return 16 + Math.max(...xValues)
    }
  },
  methods: {
    onMouseEnter (event) {
      this.isHovered = true
      this.$emit('onMouseEnter', {
        coords: {
          x: event.x,
          y: event.y,
          bbox: event.target.getBoundingClientRect()
        }
      })
    },
    onMouseLeave () {
      this.isHovered = false
      this.$emit('onMouseLeave')
    },
    safeProduct (a, b) {
      if (a == null || b == null) {
        return null
      } else {
        return a * b
      }
    }
  }
}
</script>
